import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

export interface User {
  id: number
  username: string
  email: string
  role?: string | null
  stage?: string | null
  grade?: string | null
  province?: string | null
  city?: string | null
  district?: string | null
  isActive: boolean
}

export interface RegisterPayload {
  username: string
  email: string
  password: string
  role?: string
  stage?: string
  grade?: string
  province?: string
  city?: string
  district?: string
}

export interface LoginPayload {
  username: string
  password: string
}

export interface TokenResponse {
  accessToken: string
  tokenType: string
}

export interface UpdateUserPayload {
  username?: string
  email?: string
  role?: string
  stage?: string
  grade?: string
}

export interface ChangePasswordPayload {
  oldPassword: string
  newPassword: string
}

export interface ResetPasswordPayload {
  username: string
  email: string
  newPassword: string
}

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export async function register(data: RegisterPayload): Promise<User> {
  return tRPCRequest<User>("auth.register", data)
}

export async function login(data: LoginPayload): Promise<TokenResponse> {
  return tRPCRequest<TokenResponse>("auth.login", data)
}

export async function fetchCurrentUser(): Promise<User> {
  return tRPCRequest<User>("auth.me", undefined, "GET")
}

export async function updateCurrentUser(
  data: UpdateUserPayload,
): Promise<User> {
  return tRPCRequest<User>("auth.updateMe", data)
}

export async function changePassword(
  data: ChangePasswordPayload,
): Promise<{ success: boolean }> {
  return tRPCRequest<{ success: boolean }>("auth.changePassword", data)
}

export async function resetPassword(
  data: ResetPasswordPayload,
): Promise<{ success: boolean }> {
  return tRPCRequest<{ success: boolean }>("auth.resetPassword", data)
}
